import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  RefreshCw,
  Copy,
  Trash2,
  Check,
  AlertTriangle,
  Zap,
  ChevronDown,
  ChevronUp,
  Merge
} from "lucide-react";
import { toast } from "sonner";

export default function Duplicates() {
  const [loading, setLoading] = useState(true);
  const [dedupeData, setDedupeData] = useState(null);
  const [threshold, setThreshold] = useState(75);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedForMerge, setSelectedForMerge] = useState({});
  const [showAutoCleanDialog, setShowAutoCleanDialog] = useState(false);
  const [autoCleanThreshold, setAutoCleanThreshold] = useState(85);
  const [autoCleanPreview, setAutoCleanPreview] = useState(null);

  const fetchDuplicates = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/deduplication/find?threshold=${threshold}`);
      setDedupeData(response.data);
      // Initialize selected state - keep primary by default
      const initial = {};
      response.data.duplicate_groups?.forEach(group => {
        initial[group.primary_id] = group.primary_id;
      });
      setSelectedForMerge(initial);
    } catch (error) {
      toast.error("Failed to find duplicates");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const selectForKeep = (groupPrimaryId, leadId) => {
    setSelectedForMerge(prev => ({
      ...prev,
      [groupPrimaryId]: leadId
    }));
  };

  const handleMergeGroup = async (group) => {
    const keepId = selectedForMerge[group.primary_id];
    const removeIds = [group.primary_id, ...group.duplicates.map(d => d.id)].filter(id => id !== keepId);
    
    if (removeIds.length === 0) {
      toast.info("No duplicates to remove");
      return;
    }

    try {
      const response = await api.post('/deduplication/merge', {
        keep_id: keepId,
        remove_ids: removeIds
      });
      toast.success(response.data.message);
      fetchDuplicates();
    } catch (error) {
      toast.error("Failed to merge duplicates");
      console.error(error);
    }
  };

  const handleAutoCleanPreview = async () => {
    try {
      const response = await api.delete(
        `/deduplication/auto-clean?threshold=${autoCleanThreshold}&dry_run=true`
      );
      setAutoCleanPreview(response.data);
      setShowAutoCleanDialog(true);
    } catch (error) {
      toast.error("Failed to preview auto-clean");
      console.error(error);
    }
  };

  const handleAutoCleanExecute = async () => {
    try {
      const response = await api.delete(
        `/deduplication/auto-clean?threshold=${autoCleanThreshold}&dry_run=false`
      );
      toast.success(response.data.message);
      setShowAutoCleanDialog(false);
      setAutoCleanPreview(null);
      fetchDuplicates();
    } catch (error) {
      toast.error("Failed to auto-clean duplicates");
      console.error(error);
    }
  };

  const getSimilarityColor = (similarity) => {
    if (similarity >= 90) return "text-red-400";
    if (similarity >= 80) return "text-orange-400";
    return "text-yellow-400";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="duplicates-loading">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-[#2a9d8f] mx-auto mb-4" />
          <p className="text-[#e9c46a] font-mono">SCANNING FOR DUPLICATES...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="duplicates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-12 rounded-xl bg-gradient-to-br from-[#f4a261] to-[#e76f51] flex items-center justify-center" style={{boxShadow: '4px 4px 0 rgba(139, 69, 19, 0.4)'}}>
            <Copy className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#faf3e0]" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
              Deduplication
            </h1>
            <p className="text-[#a8c8c4] text-sm font-mono">
              FIND & MERGE DUPLICATE LEADS
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            className="border-2 border-[#e76f51]/50 text-[#e76f51] hover:bg-[#e76f51]/20"
            onClick={handleAutoCleanPreview}
            data-testid="auto-clean-btn"
          >
            <Zap className="w-4 h-4 mr-2" />
            AUTO-CLEAN
          </Button>
          <Button 
            className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] hover:from-[#40c9ba] hover:to-[#2a9d8f] text-[#faf3e0] border-2 border-[#40c9ba]/50"
            onClick={fetchDuplicates}
            data-testid="refresh-duplicates-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            SCAN
          </Button>
        </div>
      </div>

      {/* Stats & Controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-5 rounded-xl" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(42, 157, 143, 0.3)',
        }}>
          <p className="text-[#a8c8c4] text-xs font-mono mb-1">TOTAL LEADS</p>
          <p className="text-3xl font-bold text-[#40c9ba]">{dedupeData?.total_leads || 0}</p>
        </div>
        <div className="p-5 rounded-xl" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(244, 162, 97, 0.3)',
        }}>
          <p className="text-[#a8c8c4] text-xs font-mono mb-1">DUPLICATE GROUPS</p>
          <p className="text-3xl font-bold text-[#f4a261]">{dedupeData?.duplicate_groups?.length || 0}</p>
        </div>
        <div className="p-5 rounded-xl" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(231, 111, 81, 0.3)',
        }}>
          <p className="text-[#a8c8c4] text-xs font-mono mb-1">POTENTIAL DUPLICATES</p>
          <p className="text-3xl font-bold text-[#e76f51]">{dedupeData?.potential_duplicates_count || 0}</p>
        </div>
      </div>

      {/* Threshold Control */}
      <div className="p-5 rounded-xl" style={{
        background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
        border: '3px solid rgba(233, 196, 106, 0.3)',
      }}>
        <div className="flex items-center gap-6">
          <div className="flex-1">
            <Label className="text-[#e9c46a] font-mono text-xs">SIMILARITY THRESHOLD: {threshold}%</Label>
            <p className="text-xs text-[#a8c8c4] mt-1">Higher = fewer but more certain duplicates</p>
          </div>
          <Input
            type="range"
            min={50}
            max={95}
            value={threshold}
            onChange={(e) => setThreshold(parseInt(e.target.value))}
            className="w-48"
          />
          <Button 
            variant="outline" 
            size="sm"
            className="border-[#e9c46a]/50 text-[#e9c46a]"
            onClick={fetchDuplicates}
          >
            Apply
          </Button>
        </div>
      </div>

      {/* Duplicate Groups */}
      {dedupeData?.duplicate_groups?.length === 0 ? (
        <div className="p-12 rounded-xl text-center" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(42, 157, 143, 0.3)',
        }}>
          <Check className="w-16 h-16 text-[#2a9d8f] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-[#faf3e0] mb-2">No Duplicates Found</h3>
          <p className="text-[#a8c8c4] font-mono">Your leads are clean! Try lowering the threshold to find more potential matches.</p>
        </div>
      ) : (
        <ScrollArea className="h-[calc(100vh-450px)]">
          <div className="space-y-4">
            {dedupeData?.duplicate_groups?.map((group, groupIndex) => (
              <div 
                key={group.primary_id}
                className="rounded-xl overflow-hidden"
                style={{
                  background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
                  border: '3px solid rgba(244, 162, 97, 0.3)',
                }}
              >
                {/* Group Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-[#2a9d8f]/10 transition-colors"
                  onClick={() => toggleGroup(group.primary_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-[#f4a261]" />
                      <div>
                        <h3 className="text-[#faf3e0] font-semibold">{group.primary_title}</h3>
                        <p className="text-sm text-[#a8c8c4]">{group.primary_client}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className="bg-[#e76f51]/20 text-[#e76f51] border border-[#e76f51]/40 font-mono">
                        {group.duplicates.length} duplicate{group.duplicates.length > 1 ? 's' : ''}
                      </Badge>
                      <Badge className="bg-[#2a9d8f]/20 text-[#40c9ba] border border-[#2a9d8f]/40 font-mono">
                        Score: {group.primary_score}
                      </Badge>
                      {expandedGroups.has(group.primary_id) ? (
                        <ChevronUp className="w-5 h-5 text-[#a8c8c4]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#a8c8c4]" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedGroups.has(group.primary_id) && (
                  <div className="border-t-2 border-[#f4a261]/30 p-4 space-y-3">
                    {/* Primary Lead */}
                    <div 
                      className={`p-4 rounded-lg cursor-pointer transition-all ${
                        selectedForMerge[group.primary_id] === group.primary_id
                          ? 'bg-[#2a9d8f]/30 border-2 border-[#2a9d8f]'
                          : 'bg-[#264653]/50 border-2 border-transparent hover:border-[#2a9d8f]/50'
                      }`}
                      onClick={() => selectForKeep(group.primary_id, group.primary_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {selectedForMerge[group.primary_id] === group.primary_id && (
                            <Check className="w-5 h-5 text-[#2a9d8f]" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-[#faf3e0] font-medium">{group.primary_title}</span>
                              <Badge className="bg-[#e9c46a]/20 text-[#e9c46a] text-xs">PRIMARY</Badge>
                            </div>
                            <p className="text-sm text-[#a8c8c4]">{group.primary_client}</p>
                          </div>
                        </div>
                        <span className="text-[#40c9ba] font-mono font-bold">Score: {group.primary_score}</span>
                      </div>
                    </div>

                    {/* Duplicate Leads */}
                    {group.duplicates.map((dup, dupIndex) => (
                      <div 
                        key={dup.id}
                        className={`p-4 rounded-lg cursor-pointer transition-all ${
                          selectedForMerge[group.primary_id] === dup.id
                            ? 'bg-[#2a9d8f]/30 border-2 border-[#2a9d8f]'
                            : 'bg-[#264653]/50 border-2 border-transparent hover:border-[#2a9d8f]/50'
                        }`}
                        onClick={() => selectForKeep(group.primary_id, dup.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {selectedForMerge[group.primary_id] === dup.id && (
                              <Check className="w-5 h-5 text-[#2a9d8f]" />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-[#faf3e0] font-medium">{dup.title}</span>
                                <Badge className={`text-xs ${getSimilarityColor(group.similarity_scores[dupIndex])}`}>
                                  {group.similarity_scores[dupIndex]}% match
                                </Badge>
                              </div>
                              <p className="text-sm text-[#a8c8c4]">{dup.client} • {dup.source}</p>
                            </div>
                          </div>
                          <span className="text-[#40c9ba] font-mono font-bold">Score: {dup.score}</span>
                        </div>
                      </div>
                    ))}

                    {/* Merge Button */}
                    <Button 
                      className="w-full bg-gradient-to-r from-[#f4a261] to-[#e76f51] hover:from-[#e9c46a] hover:to-[#f4a261] text-white font-bold"
                      onClick={() => handleMergeGroup(group)}
                    >
                      <Merge className="w-4 h-4 mr-2" />
                      MERGE - Keep Selected, Remove {group.duplicates.length} Duplicate{group.duplicates.length > 1 ? 's' : ''}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Auto-Clean Dialog */}
      <AlertDialog open={showAutoCleanDialog} onOpenChange={setShowAutoCleanDialog}>
        <AlertDialogContent className="bg-[#264653] border-2 border-[#e76f51]/50">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#faf3e0] flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#e76f51]" />
              Auto-Clean Duplicates
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a8c8c4]">
              {autoCleanPreview ? (
                <div className="space-y-3 mt-4">
                  <p className="text-lg">
                    Found <span className="text-[#e76f51] font-bold">{autoCleanPreview.would_remove}</span> potential duplicates at {autoCleanThreshold}% threshold.
                  </p>
                  {autoCleanPreview.duplicates_found?.length > 0 && (
                    <div className="max-h-48 overflow-y-auto bg-[#1a3a42] p-3 rounded-lg">
                      {autoCleanPreview.duplicates_found.slice(0, 10).map((dup, i) => (
                        <div key={i} className="text-sm py-1 border-b border-[#2a9d8f]/20 last:border-0">
                          <span className="text-[#faf3e0]">{dup.title.substring(0, 40)}...</span>
                          <span className="text-[#f4a261] ml-2">({dup.similarity_score}% similar)</span>
                        </div>
                      ))}
                      {autoCleanPreview.duplicates_found.length > 10 && (
                        <p className="text-[#a8c8c4] text-xs mt-2">...and {autoCleanPreview.duplicates_found.length - 10} more</p>
                      )}
                    </div>
                  )}
                  <p className="text-sm text-[#f4a261]">This action cannot be undone!</p>
                </div>
              ) : (
                <p>Loading preview...</p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1a3a42] text-[#a8c8c4] border-[#2a9d8f]/40 hover:bg-[#2a9d8f]/20">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-[#e76f51] hover:bg-[#d45d3e] text-white"
              onClick={handleAutoCleanExecute}
              disabled={!autoCleanPreview || autoCleanPreview.would_remove === 0}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {autoCleanPreview?.would_remove || 0} Duplicates
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
